import React, { useState, useRef, useEffect } from "react";
import { Modal, Upload, Input, Button, Form, Dropdown, Menu, Space, message, Spin, Tabs, Empty, Table } from "antd";
import { DownOutlined, UploadOutlined, DeleteOutlined, LoadingOutlined, FileTextOutlined } from "@ant-design/icons";
import * as Constants from "../CustomerComponents/Constants";
import ENVIRONMENT from "../../../environments";
import sanitizeFilename from "../../../utils/sanitizeFilename";

const { Dragger } = Upload;
const { TabPane } = Tabs;

const CreateProductModal = ({ visible, onCancel, onOk, mode = 'product', propsLibrary = [], showNameField = false, showCategoryField = false, nameFieldLabel = 'Prop Name', nameFieldPlaceholder = 'Enter prop name', showDimensions = true, modalTitle = null, showBulkUploadTab = false, bulkUploadType = 'prop', onBulkUploadSubmit = null }) => {
  const [form] = Form.useForm();
  const [filesList, setFilesList] = useState([]);
  const [activeTabKey, setActiveTabKey] = useState('upload');
  const [selectedLibraryProp, setSelectedLibraryProp] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState("inches");
  const [formData, setFormData] = useState({
    width: "",
    height: "",
    depth: "",
    name: "",
    category: ""
  });
  const [isPreviewHovered, setIsPreviewHovered] = useState(false);
  const [librarySearchValue, setLibrarySearchValue] = useState("");
  const [hoveredLibraryPropId, setHoveredLibraryPropId] = useState(null);
  
  // Bulk upload state
  const [bulkCsvFile, setBulkCsvFile] = useState(null);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [isBulkCsvHovered, setIsBulkCsvHovered] = useState(false);
  const [bulkUploadErrors, setBulkUploadErrors] = useState(null);

  const isPropMode = mode === 'prop';
  const isInspirationMode = mode === 'inspiration';
  const hasPropsLibrary = Array.isArray(propsLibrary) && propsLibrary.length > 0;

  // Show tabs UI when we have library tab OR bulk upload tab
  const showLibraryTab = (isPropMode || isInspirationMode) && hasPropsLibrary;
  const showTabs = showLibraryTab || showBulkUploadTab;

  const unitAbbreviation = {
    inches: 'in',
    meters: 'm',
    centimeters: 'cm',
    millimeters: 'mm',
    feet: 'ft'
  }

  const menu = (
    <Menu>
      <Menu.Item onClick={() => setSelectedUnit("inches")}>inches</Menu.Item>
      <Menu.Item onClick={() => setSelectedUnit("meters")}>meters</Menu.Item>
      <Menu.Item onClick={() => setSelectedUnit("centimeters")}>centimeters</Menu.Item>
      <Menu.Item onClick={() => setSelectedUnit("millimeters")}>millimeters</Menu.Item>
      <Menu.Item onClick={() => setSelectedUnit("feet")}>feet</Menu.Item>
    </Menu>
  );

  const validateFile = (file) => {
    const isAllowedFormat = [
      "image/png",
      "image/jpeg",
      "image/webp",
      "image/heic",
      "image/tiff"
    ].includes(file.type);

    if (!isAllowedFormat) {
      message.error("Only .png, .jpg, .webp, .heic, and .tiff formats are allowed.");
      return false;
    }

    return true;
  };

  const uploadProps = {
    ...Constants.upload_props,
    multiple: false,
    accept: ".jpg,.jpeg,.png,.tiff,.webp,.heic",
    beforeUpload: (file) => {
      return validateFile(file);
    },
    customRequest: (options) => {
      const { file, ...rest } = options;
      const cleanName = sanitizeFilename(file.name);
      const sanitizedFile = new File([file], cleanName, { type: file.type });
      sanitizedFile.uid = file.uid;
      Constants.upload_props.customRequest({ ...rest, file: sanitizedFile });
    },
    fileList: filesList,
    showUploadList: false, // Hide default upload list
    onChange(info) {
      const sanitizedFile = { ...info.file, name: sanitizeFilename(info.file.name) };
      setFilesList([sanitizedFile]);

      const { status } = info.file;
      if (status === "done") {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === "error") {
        if (info.file.error.code.includes('Credentials')) {
          message.error('Your session has expired. Please reload the page.');
        } else {
          message.error(`${info.file.name} file upload failed.`);
        }
      }
    },
    onDrop(e) {
      console.log("Dropped files", e.dataTransfer.files);
    },
  };

  // CSV upload props for bulk upload
  const csvUploadProps = {
    ...Constants.upload_props,
    multiple: false,
    accept: ".csv",
    beforeUpload: (file) => {
      const isCsv = file.type === "text/csv" || file.name.endsWith('.csv');
      if (!isCsv) {
        message.error("Only CSV files are allowed.");
        return false;
      }
      return true;
    },
    fileList: bulkCsvFile ? [bulkCsvFile] : [],
    showUploadList: false,
    onChange(info) {
      if (info.file.status === "done") {
        setBulkCsvFile(info.file);
        message.success(`${info.file.name} uploaded successfully.`);
      } else if (info.file.status === "error") {
        if (info.file.error?.code?.includes('Credentials')) {
          message.error('Your session has expired. Please reload the page.');
        } else {
          message.error(`${info.file.name} upload failed.`);
        }
      } else if (info.file.status === "uploading") {
        setBulkCsvFile(info.file);
      }
    },
  };

  // Handle bulk upload submission
  const handleBulkUploadSubmit = async () => {
    // Validate CSV file
    if (!bulkCsvFile || bulkCsvFile.status !== "done") {
      message.error("Please upload a CSV file.");
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadErrors(null);
    
    try {
      const payload = {
        csvFile: bulkCsvFile,
        requestName: 'Untitled',
        uploadType: bulkUploadType,
      };
      
      console.log('Bulk upload payload:', payload);
      
      // Call the callback if provided - expects { success, errors } return
      if (onBulkUploadSubmit) {
        const result = await onBulkUploadSubmit(payload);
        
        if (result && result.success === false && result.errors && result.errors.length > 0) {
          // Show errors in the table
          setBulkUploadErrors(result.errors);
          return;
        }
      }
      
      // On success - reset state and close modal
      setBulkCsvFile(null);
      setBulkUploadErrors(null);
      setActiveTabKey('upload');
      onCancel();
    } catch (error) {
      console.error('Bulk upload error:', error);
      message.error('Failed to submit bulk upload request.');
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // Reset bulk upload state (for re-upload after errors)
  const resetBulkUploadState = () => {
    setBulkCsvFile(null);
    setBulkUploadErrors(null);
  };

  // Error table columns
  const bulkErrorColumns = [
    {
      title: "Error",
      dataIndex: "message",
      key: "message",
    },
  ];

  const getLibraryPropUri = (prop) => {
    return prop?.image_uri || '';
  };

  const getLibraryPropThumbUri = (prop) => {
    return prop?.thumbnail || prop?.image_uri || '';
  };

  const getDisplayImageSrc = (uri) => {
    if (!uri) return '';
    const u = String(uri);
    if (u.startsWith('http://') || u.startsWith('https://')) return u;
    const base = ENVIRONMENT.getBaseURL('aws');
    // Avoid double-encoding already-encoded URIs (they often contain %2F).
    const suffix = u.includes('%') ? u : encodeURIComponent(u);
    return base + suffix;
  };

  const handleSelectLibraryProp = (prop) => {
    setSelectedLibraryProp(prop);
    setFilesList([]);
  };

  const filteredPropsLibrary = React.useMemo(() => {
    if (!hasPropsLibrary) return [];
    const q = (librarySearchValue || '').trim().toLowerCase();
    if (!q) return propsLibrary;
    return propsLibrary.filter((p) => {
      const idStr = (p?.id != null ? String(p.id) : '').toLowerCase();
      const nameStr = String(p?.name || '').toLowerCase() || String(p?.identifier || '').toLowerCase();
      const categoryStr = String(p?.category || '').toLowerCase();
      return idStr.includes(q) || nameStr.includes(q) || categoryStr.includes(q);
    });
  }, [propsLibrary, hasPropsLibrary, librarySearchValue]);

  const handleFormSubmit = async () => {
    try {
      // Validate prop/product selection (Upload tab requires file; Library tab requires selection)
      const isLibraryFlow = showLibraryTab && activeTabKey === 'library';
      if (isLibraryFlow) {
        if (!selectedLibraryProp) {
          message.error("Please select a prop from the library.");
          return;
        }

        // For props from library, we don't ask for dimensions.
        const payload = {
          files: [],
          dimensions: null,
          source: 'library',
          libraryItem: selectedLibraryProp,
        };

        form.resetFields();
        setFilesList([]);
        setSelectedLibraryProp(null);
        setActiveTabKey('upload');
        setFormData({ width: "", height: "", depth: "" });
        onOk(payload);
        return;
      } else {
        if (filesList.length === 0) {
          message.error(isPropMode ? "Please upload a prop photo." : isInspirationMode ? "Please upload an inspiration photo." : "Please upload a product photo.");
          return;
        }
        if (filesList.some(file => file.status === "uploading")) {
          message.error("Please wait for the upload to complete.");
          return;
        }
        if (filesList.some(file => file.status === "error")) {
          message.error("Please fix upload errors before proceeding.");
          return;
        }
      }

      // Validate form fields first (upload flow only)
      await form.validateFields();

      // If all validations pass, get the values and call onOk
      const values = form.getFieldsValue();
      const { width, height, depth, name, category } = values;

      const payload = {
        files: filesList.map(f => ({ ...f, name: sanitizeFilename(f.name) })),
        source: 'upload',
        libraryItem: null,
      };

      // Add dimensions if they were requested
      if (showDimensions) {
        payload.dimensions = {
          width: parseFloat(width),
          height: parseFloat(height),
          depth: parseFloat(depth),
          unit: selectedUnit
        };
      }

      // Add name and category if they were requested
      if (showNameField) {
        payload.name = name;
      }
      if (showCategoryField) {
        payload.category = category;
      }

      form.resetFields();
      setFilesList([]);
      setSelectedLibraryProp(null);
      setActiveTabKey('upload');
      setFormData({ width: "", height: "", depth: "", name: "", category: "" });
      onOk(payload);
    } catch (errorInfo) {
      // Form validation failed - the error messages will be shown automatically
      console.log('Validation failed:', errorInfo);
    }
  };

  const handleCancel = () => {
    // Reset form and files when canceling
    form.resetFields();
    setFilesList([]);
    setSelectedLibraryProp(null);
    setActiveTabKey('upload');
    setFormData({ width: "", height: "", depth: "", name: "", category: "" });
    // Reset bulk upload state
    setBulkCsvFile(null);
    setBulkUploadErrors(null);
    onCancel();
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleCancel}
      width={520}
      centered
      footer={null}
      style={{ top: 20 }}
    >
      <div style={{ padding: '0', marginTop: '16px' }}>
        <h4 className="manrope f-20 w-500 black-00" style={{marginBottom: '24px'}} >
          {modalTitle || (isPropMode ? 'Add a Prop' : 'Add a Product Photo')}
        </h4>

        {/* Upload / Library Section */}
        <div style={{ marginBottom: '32px' }}>
          {showTabs ? (
            <Tabs
              activeKey={activeTabKey}
              onChange={setActiveTabKey}
              style={{ width: '100%' }}
              tabBarStyle={{ width: '100%' }}
            >
              <TabPane tab="Upload" key="upload">
                {filesList.length === 0 ? (
                  <Dragger
                    {...uploadProps}
                    style={{
                      border: "1px dashed #595959",
                      background: "white",
                      borderRadius: 8,
                      padding: '32px 24px',
                      textAlign: "center",
                      minHeight: '140px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <div className="manrope" style={{ width: '100%' }}>
                      <div style={{ color: "black", fontSize: '16px', marginBottom: '12px', fontWeight: '400' }}>
                        Drag & Drop or
                      </div>
                      <Button icon={<UploadOutlined />} className="upload-photo-btn">
                        Upload photo
                      </Button>
                      <div style={{ fontSize: 12, color: "#595959", marginTop: '12px', lineHeight: '1.4' }}>
                        {isPropMode ? 'Upload a prop image.' : isInspirationMode ? 'Upload an inspiration image.' : 'Products on clean backgrounds preferred.'}
                      </div>
                      <div style={{ fontSize: 12, color: "#595959", lineHeight: '1.4' }}>
                        Supported formats: JPG, PNG, WebP, HEIC, or TIFF.
                      </div>
                    </div>
                  </Dragger>
                ) : (
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {filesList.map((file) => (
                      <div
                        key={file.uid}
                        style={{
                          position: 'relative',
                          width: '132px',
                          height: '132px',
                          border: '1px solid #D9D9D9',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: 'white'
                        }}
                        onMouseEnter={() => setIsPreviewHovered(true)}
                        onMouseLeave={() => setIsPreviewHovered(false)}
                      >
                        {file.status === "uploading" ? (
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            height: '100%',
                            backgroundColor: '#f5f5f5'
                          }}>
                            <Spin indicator={<LoadingOutlined spin />} style={{color: '#8C8C8C'}} size="large" />
                          </div>
                        ) : (
                          <>
                            <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                              <img
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'contain'
                                }}
                                src={ENVIRONMENT.getBaseURL('aws') + encodeURIComponent("test_files" + "/" + file.uid + "/" + sanitizeFilename(file.name))}
                                alt={file.name}
                              />
                            </div>
                            <Button
                              onClick={() => {
                                setFilesList([]);
                              }}
                              style={{
                                position: 'absolute',
                                top: '8px',
                                left: '8px',
                                width: '24px',
                                height: '24px',
                                minWidth: '24px',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: '#000000',
                                borderRadius: '4px',
                                border: 'none',
                                opacity: isPreviewHovered ? 1 : 0,
                                transition: 'opacity 120ms ease-in-out'
                              }}
                            >
                              <DeleteOutlined style={{ color: 'white', fontSize: '12px' }} />
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </TabPane>
              {showLibraryTab && (
              <TabPane tab="From Library" key="library">
                {propsLibrary.length === 0 ? (
                  <Empty description="No props available in library." />
                ) : (
                  <div style={{ width: '100%' }}>
                    <Input
                      placeholder={isInspirationMode ? "Search inspirations..." : "Search props..."}
                      value={librarySearchValue}
                      onChange={(e) => setLibrarySearchValue(e.target.value)}
                      className="manrope f-14 mat-search-bar"
                      style={{ marginBottom: 12, height: 40 }}
                    />

                    <div style={{ width: '100%', maxHeight: 320, overflowY: 'auto' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
                    {filteredPropsLibrary.map((p, idx) => {
                      const uri = getLibraryPropThumbUri(p);
                      const selected = (selectedLibraryProp?.id != null && p?.id != null)
                        ? String(selectedLibraryProp.id) === String(p.id)
                        : selectedLibraryProp === p;
                      
                      // Format dimensions similar to PropsLibrary
                      const formatDimensions = () => {
                        if (!p.width && !p.height && !p.depth) return null;
                        const parts = [];
                        if (p.width) parts.push(`${p.width}"W`);
                        if (p.height) parts.push(`${p.height}"H`);
                        if (p.depth) parts.push(`${p.depth}"D`);
                        return parts.join(' x ');
                      };
                      
                      const dimensions = formatDimensions();
                      
                      return (
                        <div
                          key={p?.id != null ? `prop-lib-${p.id}` : `prop-lib-idx-${idx}`}
                          onClick={() => handleSelectLibraryProp(p)}
                          onMouseEnter={() => setHoveredLibraryPropId(p?.id ?? idx)}
                          onMouseLeave={() => setHoveredLibraryPropId(null)}
                          style={{
                            width: '100%',
                            aspectRatio: '1 / 1',
                            border: selected ? '2px solid #276DD7' : '1px solid #D9D9D9',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            position: 'relative'
                          }}
                        >
                          <div style={{ 
                            flex: 1, 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            overflow: 'hidden'
                          }}>
                            {uri ? (
                              <img
                                src={getDisplayImageSrc(uri)}
                                alt="Prop"
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div className="manrope f-12" style={{ color: '#8C8C8C', padding: 8, textAlign: 'center' }}>
                                No preview
                              </div>
                            )}
                          </div>
                           {dimensions && hoveredLibraryPropId === (p?.id ?? idx) && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              padding: '4px 8px',
                              fontSize: '10px',
                              fontFamily: 'Manrope',
                              fontWeight: 400,
                              textAlign: 'center',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {dimensions}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    </div>
                    </div>
                  </div>
                )}
              </TabPane>
              )}
              {showBulkUploadTab && (
                <TabPane tab="Bulk Upload" key="bulk_upload">
                  {bulkUploadErrors ? (
                    // Error display view
                    <div>
                      <div style={{ marginBottom: 16 }}>
                        <p className="manrope f-14">The uploaded CSV contains errors. Please fix them and re-upload.</p>
                        <Table
                          dataSource={bulkUploadErrors}
                          columns={bulkErrorColumns}
                          pagination={false}
                          scroll={{ y: 300 }}
                          rowKey="row"
                          size="small"
                          bordered
                        />
                      </div>
                      <div className="justify-in-end">
                        <Button onClick={resetBulkUploadState} className="modal-okay square font-14">
                          Re-upload
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // Normal upload form
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      {/* Download Sample CSV Link */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <a 
                          href={bulkUploadType === 'inspiration' 
                            ? "https://d48f7equ64qjl.cloudfront.net/static_files/bulk-inspirations-template.csv"
                            : "https://d48f7equ64qjl.cloudfront.net/static_files/bulk-props-template.csv"
                          } 
                          className="manrope f-14" 
                          style={{ color: "#1890ff" }}
                          download
                        >
                          Download CSV template
                        </a>
                      </div>
                      {/* CSV Upload Dragger */}
                      {!bulkCsvFile ? (
                        <Dragger
                          {...csvUploadProps}
                          style={{
                            border: "1px dashed #595959",
                            background: "white",
                            borderRadius: 8,
                            padding: '32px 24px',
                            textAlign: "center",
                            minHeight: '140px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <div className="manrope" style={{ width: '100%' }}>
                            <div style={{ color: "black", fontSize: '16px', marginBottom: '12px', fontWeight: '400' }}>
                              Drag & Drop or
                            </div>
                            <Button icon={<UploadOutlined />} className="upload-photo-btn">
                              Upload CSV
                            </Button>
                            <div style={{ fontSize: 12, color: "#595959", marginTop: '12px', lineHeight: '1.4' }}>
                              Upload a CSV file with {bulkUploadType === 'inspiration' ? 'inspirations' : 'props'} data.
                            </div>
                            <div style={{ fontSize: 12, color: "#595959", lineHeight: '1.4' }}>
                              Supported format: CSV
                            </div>
                          </div>
                        </Dragger>
                      ) : (
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                          <div
                            style={{
                              position: 'relative',
                              width: '132px',
                              height: '132px',
                              border: '1px solid #D9D9D9',
                              borderRadius: '8px',
                              overflow: 'hidden',
                              backgroundColor: 'white'
                            }}
                            onMouseEnter={() => setIsBulkCsvHovered(true)}
                            onMouseLeave={() => setIsBulkCsvHovered(false)}
                          >
                            {bulkCsvFile.status === "uploading" ? (
                              <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                height: '100%',
                                backgroundColor: '#f5f5f5'
                              }}>
                                <Spin indicator={<LoadingOutlined spin />} style={{color: '#8C8C8C'}} size="large" />
                              </div>
                            ) : (
                              <>
                                <div style={{ width: '100%', height: '100%', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                                  <FileTextOutlined style={{ fontSize: '32px', color: '#8C8C8C', marginBottom: '8px' }} />
                                  <span className="manrope f-12" style={{ padding: '0 8px', textAlign: 'center', wordBreak: 'break-word' }}>
                                    {bulkCsvFile.name.length > 20 
                                      ? bulkCsvFile.name.substring(0, 12) + '...' + bulkCsvFile.name.substring(bulkCsvFile.name.length - 8) 
                                      : bulkCsvFile.name}
                                  </span>
                                </div>
                                <Button
                                  onClick={() => setBulkCsvFile(null)}
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    left: '8px',
                                    width: '24px',
                                    height: '24px',
                                    minWidth: '24px',
                                    padding: '0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#000000',
                                    borderRadius: '4px',
                                    border: 'none',
                                    opacity: isBulkCsvHovered ? 1 : 0,
                                    transition: 'opacity 120ms ease-in-out'
                                  }}
                                >
                                  <DeleteOutlined style={{ color: 'white', fontSize: '12px' }} />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Submit Button */}
                      <div className="justify-in-end" style={{ marginTop: '8px' }}>
                        <Button
                          style={{ marginRight: 10 }}
                          className="modal-okay-gray square font-14 create-product-modal-cancel"
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleBulkUploadSubmit}
                          className="modal-okay square font-14 create-product-modal-add"
                          loading={bulkUploadLoading}
                          disabled={bulkCsvFile && bulkCsvFile.status === 'uploading'}
                        >
                          Add
                        </Button>
                      </div>
                    </div>
                  )}
                </TabPane>
              )}
            </Tabs>
          ) : (
            <>
              {filesList.length === 0 ? (
                <Dragger
                  {...uploadProps}
                  style={{
                    border: "1px dashed #595959",
                    background: "white",
                    borderRadius: 8,
                    padding: '32px 24px',
                    textAlign: "center",
                    minHeight: '140px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div className="manrope" style={{ width: '100%' }}>
                    <div style={{ color: "black", fontSize: '16px', marginBottom: '12px', fontWeight: '400' }}>
                      Drag & Drop or
                    </div>
                    <Button icon={<UploadOutlined />} className="upload-photo-btn">
                      Upload photo
                    </Button>
                    <div style={{ fontSize: 12, color: "#595959", marginTop: '12px', lineHeight: '1.4' }}>
                      {isPropMode ? 'Upload a prop image.' : isInspirationMode ? 'Upload an inspiration image.' : 'Products on clean backgrounds preferred.'}
                    </div>
                    <div style={{ fontSize: 12, color: "#595959", lineHeight: '1.4' }}>
                      Supported formats: JPG, PNG, WebP, HEIC, or TIFF.
                    </div>
                  </div>
                </Dragger>
              ) : (
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                  {filesList.map((file) => (
                    <div
                      key={file.uid}
                      style={{
                        position: 'relative',
                        width: '132px',
                        height: '132px',
                        border: '1px solid #D9D9D9',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: 'white'
                      }}
                      onMouseEnter={() => setIsPreviewHovered(true)}
                      onMouseLeave={() => setIsPreviewHovered(false)}
                    >
                      {file.status === "uploading" ? (
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          height: '100%',
                          backgroundColor: '#f5f5f5'
                        }}>
                          <Spin indicator={<LoadingOutlined spin />} style={{color: '#8C8C8C'}} size="large" />
                        </div>
                      ) : (
                        <>
                          <div style={{ width: '100%', height: '100%', overflow: 'hidden' }}>
                            <img
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain'
                              }}
                              src={ENVIRONMENT.getBaseURL('aws') + encodeURIComponent("test_files" + "/" + file.uid + "/" + sanitizeFilename(file.name))}
                              alt={file.name}
                            />
                          </div>
                          <Button
                            onClick={() => {
                              setFilesList([]);
                            }}
                            style={{
                              position: 'absolute',
                              top: '8px',
                              left: '8px',
                              width: '24px',
                              height: '24px',
                              minWidth: '24px',
                              padding: '0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              backgroundColor: '#000000',
                              borderRadius: '4px',
                              border: 'none',
                              opacity: isPreviewHovered ? 1 : 0,
                              transition: 'opacity 120ms ease-in-out'
                            }}
                          >
                            <DeleteOutlined style={{ color: 'white', fontSize: '12px' }} />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        {/* Name and Category Section */}
        {(showNameField || showCategoryField) && !(isPropMode && showLibraryTab && activeTabKey === 'library') && activeTabKey !== 'bulk_upload' && (
          <Form form={form} layout="vertical" style={{ marginBottom: '24px' }}>
            {showNameField && (
              <Form.Item
                name="name"
                label={<span className="manrope f-14 w-400" style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{nameFieldLabel}</span>}
                rules={[{ required: true, message: `Please enter ${nameFieldLabel.toLowerCase()}` }]}
              >
                <Input
                  placeholder={nameFieldPlaceholder}
                  style={{ height: '40px' }}
                  className="manrope f-14"
                />
              </Form.Item>
            )}
            {showCategoryField && (
              <Form.Item
                name="category"
                label={<span className="manrope f-14 w-400" style={{ color: 'rgba(0, 0, 0, 0.85)' }}>Category</span>}
              >
                <Input
                  placeholder="Enter category"
                  style={{ height: '40px' }}
                  className="manrope f-14"
                />
              </Form.Item>
            )}
          </Form>
        )}

        {/* Dimensions Section (not needed for props-from-library selection or bulk upload) */}
        {showDimensions && !(isPropMode && showLibraryTab && activeTabKey === 'library') && activeTabKey !== 'bulk_upload' && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: '16px' }}>
            <span className="manrope f-14 w-400" style={{ color: 'rgba(0, 0, 0, 0.85)' }}>
              {isPropMode ? 'Prop dimensions' : 'Product dimensions'}
            </span>
            <Dropdown overlay={menu} trigger={["click"]}>
                <a onClick={(e) => e.preventDefault()}>
                  <Space className="manrope f-14 w-500">
                    {selectedUnit}
                    <DownOutlined style={{ fontSize: '12px' }} />
                  </Space>
                </a>
              </Dropdown>
        </div>

          <Form form={form} layout="vertical">
            <div style={{ display: "flex", gap: "12px", marginBottom: '16px' }}>
              <Form.Item 
                name="width" 
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Please enter width" }]}
              >
                <Input 
                  type="number" 
                  placeholder="Width" 
                  suffix={<span className="manrope f-12 w-400" style={{ color: '#8C8C8C' }}>{unitAbbreviation[selectedUnit]}</span>}
                  min="0"
                  style={{ height: '40px' }}
                  onKeyPress={(e) => {
                    if (e.key === "-" || e.key === "+") {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item 
                name="height" 
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Please enter height" }]}
              >
                <Input 
                  type="number" 
                  placeholder="Height" 
                  suffix={<span className="manrope f-12 w-400" style={{ color: '#8C8C8C' }}>{unitAbbreviation[selectedUnit]}</span>}
                  min="0"
                  style={{ height: '40px' }}
                  onKeyPress={(e) => {
                    if (e.key === "-" || e.key === "+") {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>

              <Form.Item 
                name="depth" 
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Please enter depth" }]}
              >
                <Input 
                  type="number" 
                  placeholder="Depth" 
                  suffix={<span className="manrope f-12 w-400" style={{ color: '#8C8C8C' }}>{unitAbbreviation[selectedUnit]}</span>}
                  min="0"
                  style={{ height: '40px' }}
                  onKeyPress={(e) => {
                    if (e.key === "-" || e.key === "+") {
                      e.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </div>
          </Form>
        </div>
        )}

        {activeTabKey !== 'bulk_upload' && (
        <div className="justify-in-end">
            <Button
                style={{ marginRight: 10 }}
                className="modal-okay-gray square font-14 create-product-modal-cancel"
                onClick={handleCancel}
            >
                Cancel
            </Button>
            <Button 
                onClick={handleFormSubmit} 
                className="modal-okay square font-14 create-product-modal-add"
            >
                Add
            </Button>
        </div>
        )}
      </div>
    </Modal>
  );
};

export default CreateProductModal;
